import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  SquaresFour,
  Package,
  ArrowsLeftRight,
  BookOpen,
  ChartLineUp,
  Bell,
  Ranking as RankingIcon,
  FileText,
  ShieldCheck,
  Receipt,
  EnvelopeSimple,
  AddressBook,
  SignOut,
  List,
  X,
} from "@phosphor-icons/react";

const LOGO = "/novuspak-logo.png";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true, testid: "nav-dashboard" },
  { to: "/productos", label: "Productos", icon: Package, testid: "nav-productos" },
  { to: "/movimientos", label: "Movimientos", icon: ArrowsLeftRight, testid: "nav-movimientos" },
  { to: "/kardex", label: "Kardex", icon: BookOpen, testid: "nav-kardex" },
  { to: "/inventario", label: "Análisis inventario", icon: ChartLineUp, testid: "nav-inventario" },
  { to: "/integridad", label: "Integridad", icon: ShieldCheck, testid: "nav-integridad" },
  { to: "/proveedores", label: "Proveedores", icon: AddressBook, testid: "nav-proveedores" },
  { to: "/cuentas-por-pagar", label: "Cuentas por pagar", icon: Receipt, testid: "nav-cuentas" },
  { to: "/alertas", label: "Alertas", icon: Bell, testid: "nav-alertas" },
  { to: "/ranking", label: "Ranking", icon: RankingIcon, testid: "nav-ranking" },
  { to: "/reporte", label: "Reporte mensual", icon: FileText, testid: "nav-reporte" },
  { to: "/notificaciones", label: "Notificaciones", icon: EnvelopeSimple, testid: "nav-notificaciones" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-6 h-16 border-b border-[#E2E8F0]">
        <img src={LOGO} alt="NovusPak" className="h-10 w-10 object-contain shrink-0" />
        <span className="font-heading font-extrabold text-[#0F172A] tracking-tight leading-tight">
          Control de
          <br />
          Inventario - NovusPak 2026
        </span>
      </div>
      <nav className="flex-1 px-3 py-6 space-y-1">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-[#0F172A] text-white"
                    : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
                }`
              }
            >
              <Icon size={20} weight="bold" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-[#E2E8F0] p-4">
        <div className="px-2 mb-3">
          <p className="text-sm font-semibold text-[#0F172A] truncate">{user?.name}</p>
          <p className="text-xs text-[#64748B] truncate">{user?.email}</p>
        </div>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm font-medium text-[#475569] hover:bg-[#FEF2F2] hover:text-[#991B1B] transition-all duration-200"
        >
          <SignOut size={18} weight="bold" />
          Cerrar sesión
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-[#E2E8F0] fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-white border-r border-[#E2E8F0]">
            <SidebarContent />
          </div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-20 h-16 bg-white/70 backdrop-blur-xl border-b border-[#E2E8F0] flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="NovusPak" className="h-8 w-8 object-contain shrink-0" />
            <span className="font-heading font-extrabold text-[#0F172A]">Inventario</span>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="p-2 rounded-md hover:bg-[#F1F5F9]"
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
